import { z } from 'zod'

import {
  idSchema,
  isoDateSchema,
  isoInstantSchema,
  moneyPaisa,
  searchQuerySchema,
  trashEntitySchema,
} from '../common'
import { cursorQuerySchema } from '../pagination'

/**
 * O5 · `GET /trash` · `POST /trash/:entity/:id/restore` (G11).
 *
 * Nothing the trader can destroy is destroyed: every such table carries
 * `deleted_at`, and the row stays restorable for 90 days. A man who deleted the
 * wrong animal at a mandi at midnight needs it back in the morning, and "it is
 * gone" is not an answer a ledger may give.
 */
export const trashItemSchema = z.object({
  entity: trashEntitySchema,
  id: idSchema,
  label: z.string(),
  /** A second line of context — the animal an expense belonged to, say. */
  sub: z.string().nullish(),
  amountPaisa: moneyPaisa(),
  deletedAt: isoInstantSchema,
  deletedByName: z.string().nullish(),
  /** The day it stops being restorable. 90 days after deletion. */
  purgeOn: isoDateSchema,
})

export const trashListQuery = cursorQuerySchema.extend({
  entity: trashEntitySchema.optional(),
  q: searchQuerySchema.optional(),
})

export const trashListResponse = z.array(trashItemSchema)

/**
 * Restoring an animal restores what belongs to it. The response says what came
 * back, so the confirmation can be specific rather than "restored".
 */
export const trashRestoreResponse = z.object({
  entity: trashEntitySchema,
  id: idSchema,
  restored: z.literal(true),
  /** Child rows revived alongside it — expenses, media, weights. */
  cascadedCount: z.number().int().nonnegative(),
})

export type TrashItem = z.infer<typeof trashItemSchema>
export type TrashListQuery = z.input<typeof trashListQuery>
export type TrashRestoreResponse = z.infer<typeof trashRestoreResponse>
